import {
	AWSConnectProctorSharedEvents,
	ConnectAgentDetails,
	ConnectContactState,
	ContactSessionAttributes
} from "@emed-labs/virtual-lab-models";
import { Config } from "@emed/virtual-lab-ui-shared/environment/config";
import { UseEventAddRemoveListenerMethods, useEventListener } from "@emed/virtual-lab-ui-shared/hooks/UseEventListener";
import { SoundCatalog, useSound } from "@emed/virtual-lab-ui-shared/hooks/UseSound";
import { useStateRef } from "@emed/virtual-lab-ui-shared/hooks/UseStateRef";
import { AgentUIExtraNavigatorAttributes } from "@emed/virtual-lab-ui-shared/models/Models";
import { EnvironmentContext } from "@emed/virtual-lab-ui-shared/providers/EnvironmentProvider";
import { useLogger } from "@emed/virtual-lab-ui-shared/providers/LogProvider";
import { ModalContext } from "@emed/virtual-lab-ui-shared/providers/ModalProvider";
import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";

export interface AWSConnectAgentContext extends UseEventAddRemoveListenerMethods {
	/**
	 * Agent/Proctor is authenticated
	 */
	authenticated: boolean;
	contactAttributes?: Partial<ContactSessionAttributes>;
	contactID?: string;
	contactActive: boolean;
	contactState?: connect.ContactState;
	loadingContact: boolean;
	defaultAttributes?: AgentUIExtraNavigatorAttributes;
	initializeAgent: () => Promise<any>;
	proctorApplicationRef: React.MutableRefObject<HTMLIFrameElement>;
	connectAgentContainerRef: React.MutableRefObject<HTMLDivElement>;
}

export const AWSConnectAgentContext = createContext<AWSConnectAgentContext>({} as AWSConnectAgentContext);
AWSConnectAgentContext.displayName = "AWSConnectAgentContext";

export const AWSConnectAgentGlobalProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const EnvCtx = useContext(EnvironmentContext);
	const ModalCtx = useContext(ModalContext);
	const Modal = useContext(ModalContext);
	const logger = useLogger("providers.AWSConnectAgentGlobalProvider");

	const proctorApplicationRef = useRef<HTMLIFrameElement>();
	const ALLOWED_PROCTOR_APPLICATION_ORIGINS = EnvCtx.get(Config.ALLOWED_PROCTOR_APPLICATION_ORIGINS);
	const { addListener, removeListener, dispatchEvent } = useEventListener(
		AWSConnectProctorSharedEvents,
		ALLOWED_PROCTOR_APPLICATION_ORIGINS.split(","),
		proctorApplicationRef
	);
	const IncomingSound = useSound(SoundCatalog.Incoming);
	const connectAgentContainerRef = useRef<HTMLDivElement>();
	const agent = useRef<connect.Agent>();
	const chatConnection = useRef<connect.ChatConnection>();
	const switchingContact = useRef<boolean>(false);
	const pendingContactAccepted = useRef<boolean>(false);

	const [contactID, setContactID, contactIDRef] = useStateRef<string>();
	const [contactState, setContactState] = useStateRef<connect.ContactState>();
	const [contactActive, setContactActive] = useState(false);
	const [contactAttributes, setContactAttributes, contactAttributesRef] =
		useStateRef<Partial<ContactSessionAttributes>>();

	const [authenticated, setAuthenticated] = useState<boolean>(false);
	const [loadingContact, setLoadingContact] = useState<boolean>(false);

	useEffect(() => {
		if (typeof ALLOWED_PROCTOR_APPLICATION_ORIGINS !== "string") {
			logger.error("useEffect", "ALLOWED_PROCTOR_APPLICATION_ORIGINS not set.");
			Modal.alert(`An internal error occurred. ALLOWED_PROCTOR_APPLICATION_ORIGINS is null.`);
		}
	}, [ALLOWED_PROCTOR_APPLICATION_ORIGINS]);

	useEffect(() => {
		return addListener(AWSConnectProctorSharedEvents.PROCTOR_CHILD_READY, initiateProctorLogin);
	}, []);

	/**
	 * when the child is logged in, we need to broadcast the transcript messages
	 */
	useEffect(() => {
		return addListener(AWSConnectProctorSharedEvents.PROCTOR_CHILD_LOGGED_IN, broadcastTranscriptMessages);
	}, []);

	useEffect(() => {
		return addListener(AWSConnectProctorSharedEvents.PROCTOR_GLOBAL_SEND_MESSAGE, async (data) => {
			logger.debug("PROCTOR_GLOBAL_SEND_MESSAGE", "received PROCTOR_GLOBAL_SEND_MESSAGE", { data });
			sendChatMessage(data);
		});
	}, []);

	/**
	 * Dispatch CONTACT_ACCEPTED event after state has stabilized
	 * This prevents race conditions where the event is dispatched before React has updated the UI
	 */
	useEffect(() => {
		if (contactID && contactActive && !loadingContact && chatConnection.current && pendingContactAccepted.current) {
			pendingContactAccepted.current = false;
			// Capture current values to prevent race conditions with subsequent contact switches
			const capturedContactID = contactID;
			const capturedChatConnection = chatConnection.current;
			(async () => {
				try {
					const agentState = await getLatestAgentState();
					// Verify the contact hasn't changed during the async operation
					if (contactIDRef.current === capturedContactID && chatConnection.current === capturedChatConnection) {
						logger.debug("useEffect[CONTACT_ACCEPTED]", "Dispatching CONTACT_ACCEPTED after state stabilized", { agentContactId: agentState?.contactID });
						dispatchEvent(AWSConnectProctorSharedEvents.CONTACT_ACCEPTED, agentState);
					} else {
						logger.debug("useEffect[CONTACT_ACCEPTED]", "Skipping CONTACT_ACCEPTED dispatch - contact changed during async operation", {
							capturedContactID,
							currentContactID: contactIDRef.current,
							connectionChanged: chatConnection.current !== capturedChatConnection
						});
					}
				} catch (e) {
					logger.error("useEffect[CONTACT_ACCEPTED]", "Failed to dispatch CONTACT_ACCEPTED", e);
				}
			})();
		}
	}, [contactID, contactActive, loadingContact]);

	const initiateProctorLogin = async () => {
		try {
			if (!proctorApplicationRef.current) {
				logger.warn("initiateProctorLogin", "GlobalProctorUi No proctorApplicationRef.current. Cannot  login");
				return;
			}
			const state = await getLatestAgentState();
			logger.debug("initiateProctorLogin", "GlobalProctorUi: login called.", { state });
			dispatchEvent(AWSConnectProctorSharedEvents.INITIATE_PROCTOR_LOGIN, state);
		} catch (error) {
			logger.error("initiateProctorLogin", "Unable to initiateProctorLogin", error);
		}
	};

	//GLOBAL CONTROL PANEL SIDE
	const closeAndDisconnect = () => {
		try {
			logger.debug("closeAndDisconnect", "Connect closeAndDisconnect called");
			if (agent.current) {
				let states = agent.current.getAgentStates();
				// "states" is an array of changeable states. You can filter the desired state to change by name.
				let offlineState = states.filter((state) => state.name === "Offline")[0];

				// Change agent state
				agent.current.setState(offlineState);
			}

			connect?.core?.terminate();
			logger.debug("closeAndDisconnect", "closeAndDisconnect done");
		} catch (e) {
			logger.warn("closeAndDisconnect", `Caught: ${e.message}`, e);
		}
	};
	//GLOBAL CONTROL PANEL SIDE
	const initialize = async () => {
		const { setup } = require("@emed/virtual-lab-ui-shared/ui/GlobalChatConfig");
		setup(EnvCtx.get(Config.AWS_CONNECT_REGION));
		//this is already being required in setup
		window.addEventListener("beforeunload", closeAndDisconnect);
	};

	//GLOBAL CONTROL PANEL SIDE
	const initializeAgent = async () => {
		return new Promise(async (resolve, reject) => {
			if (!connectAgentContainerRef.current) {
				reject("connectAgentContainerRef.current is null");
				return;
			}
			try {
				logger.debug("initializeAgent", "initializeAgent started");
				initialize();

				try {
					// @ts-ignore
					connect.getLog().setEchoLevel("ERROR");
				} catch (e) {
					logger.error("initializeAgent", "AgentUI initializeUI", e, {
						sessionID: contactAttributesRef.current?.sessionID,
						procedureID: contactAttributesRef.current?.procedureID
					});
				}

				connect.core.initCCP(connectAgentContainerRef.current, {
					ccpUrl: EnvCtx.get(Config.AWS_CONNECT_URL),
					loginPopup: true, // optional, defaults to `true`
					loginPopupAutoClose: true, // optional, defaults to `true`
					loginOptions: {
						// optional, if provided opens login in new window
						autoClose: true, // optional, defaults to `false`
						height: 600, // optional, defaults to 578
						width: 400, // optional, defaults to 433
						top: 0, // optional, defaults to 0
						left: 0 // optional, defaults to 0
					},
					region: EnvCtx.get(Config.AWS_CONNECT_REGION)
				});
				connect.agent((newAgent) => {
					agent.current = newAgent;
					logger.info("initializeAgent", "CONNECT AGENT AQUIRED");

					// Automatically set the agent to available
					let states = agent.current.getAgentStates();
					let availableState = states.filter((state) => state.name === "Available")[0];
					agent.current.setState(availableState);
					setAuthenticated(true);
					resolve({});
				});

				connect.contact(async (contact) => {
					try {
						IncomingSound.play();
					} catch (err) {
						logger.error("initializeAgent", "incomingChimeSound.play error: ", err);
					}

					contact.onAccepted(handleContactAccepted);
					contact.onEnded(handleContactEnded);
					contact.onDestroy(handleContactDestroyed);
					contact.onMissed((e) => {
						logger.warn("initializeAgent", "AgentUI connect.contact onMissed", {
							sessionID: contactAttributesRef.current?.sessionID,
							procedureID: contactAttributesRef.current?.procedureID,
							contactId: e.contactId
						});

						setContactActive(false);
					});
					contact.onError((e) => {
						logger.error("initializeAgent", `AgentUI connect.contact onError`, null, {
							sessionID: contactAttributesRef.current?.sessionID,
							procedureID: contactAttributesRef.current?.procedureID,
							contactID: e.contactId
						});
					});
				});

				connect.core.onViewContact(handleContactChanged);
			} catch (error) {
				reject(error);
			}
		});
	};

	/**
	 * This is primarily used in the case that the Agent reloads their webpage.
	 *
	 * Read any existing transcript, and process the messages.
	 * Check for any past user connectivity messages, and render them for agent
	 *
	 * @param chatSession
	 * @param contactID
	 */
	const broadcastTranscriptMessages = async () => {
		const chatSession = (await chatConnection.current.getMediaController()) as connect.ChatSession;
		try {
			const currentTranscript =
				(
					(
						await chatSession.getTranscript({
							maxResults: 100,
							contactId: contactIDRef.current
						})
					)?.data as connect.GetTranscriptResult
				)?.Transcript ?? [];
			logger.debug("broadcastTranscriptMessages", "reading transcript", { currentTranscript });
			for (const message of currentTranscript) {
				handleConnectChatMessage(message, "transcript");
			}
		} catch (e) {
			logger.error("broadcastTranscriptMessages", "processTranscriptMessage error", e);
		}
	};

	const clearContact = () => {
		logger.info("clearContact", "called");

		if (chatConnection.current) {
			chatConnection.current = null;
		}
		setContactID(null);
		setContactAttributes(null);
		setContactState(null);
		setContactActive(false);
	};

	const getLatestContactState = async (): Promise<ConnectContactState> => {
		const contact = getCurrentContact();
		return {
			contactID: contactIDRef.current,
			contactState: contact?.getState() as any
		};
	};

	const getLatestAgentState = async (): Promise<ConnectAgentDetails> => {
		const contact = getCurrentContact();
		const connection_token = await chatConnection.current?.getConnectionToken();
		return {
			contactID: contactIDRef.current,
			contactState: contact?.getState() as any,
			agentToken: connection_token?.chatTokenTransport?.participantToken,
			agentEmail: agent.current.getConfiguration().username,
			agentName: agent.current.getName()
		};
	};

	const validateContactAttributes = (attr: Partial<ContactSessionAttributes>) => {
		if (!attr.vLabProctorApplicationURL) {
			logger.error(
				"validateContactAttributes",
				"vLabProctorApplicationURL is required in contact attributes, and was not found."
			);
			ModalCtx.alert("vLabProctorApplicationURL is required in contact attributes, and was not found.");
		}
	};

	const switchToContact = async (newContactID: string, firstContact: boolean = false) => {
		// Prevent concurrent calls
		if (switchingContact.current) {
			logger.warn("switchToContact", "Already switching contact, ignoring call", { newContactID });
			return;
		}

		logger.info("switchToContact", "called", { newContactID, firstContact });
		switchingContact.current = true;
		clearContact();

		if (!newContactID) {
			switchingContact.current = false;
			return;
		}

		setLoadingContact(true);

		try {
			const contact = getContactByID(newContactID);
			const contactAttr: Partial<ContactSessionAttributes> = Object.fromEntries(
				Object.entries(contact.getAttributes()).map(([key, value]) => [value.name, value.value])
			);
			validateContactAttributes(contactAttr);

			// Set up chat connection and listener before updating state
			chatConnection.current = contact?.getAgentConnection() as connect.ChatConnection;
			const chatSession = (await chatConnection.current.getMediaController()) as connect.ChatSession;
			chatSession.onMessage((e) => handleConnectChatMessage(e.data, "realtime"));

			// Now update all state - this will trigger the useEffect to dispatch CONTACT_ACCEPTED
			setContactAttributes(contactAttr);
			setContactID(newContactID);
			setContactState(contact.getState());
			pendingContactAccepted.current = true;
			setContactActive(true);
			setLoadingContact(false);
		} catch (e) {
			logger.error("switchToContact", `Caught Error: ${e.message}`, e, {
				sessionID: contactAttributesRef.current?.sessionID,
				procedureID: contactAttributesRef.current?.procedureID
			});

			setLoadingContact(false);
		} finally {
			switchingContact.current = false;
		}
	};

	//GLOBAL CONTROL PANEL SIDE - NEEDS TO FORWARD TO IFRAME
	const handleConnectChatMessage = (event: connect.ChatTranscriptItem, context: "realtime" | "transcript") => {
		logger.debug("handleConnectChatMessage", "GLOBAL: INCOMING handleConnectChatMessage", { event });
		//TODO: why so locked on participant role?
		if (event.ParticipantRole === "CUSTOMER" && event.Type === "MESSAGE") {
			dispatchEvent(
				AWSConnectProctorSharedEvents.CHAT_MESSAGE_RECEIVED,
				{ content: event.Content, context },
				proctorApplicationRef.current?.contentWindow
			);
		}
	};

	/**
	 * The contact was accepted
	 * @param event
	 */
	const handleContactAccepted = (event: { contactId: string }) => {
		switchToContact(event.contactId, true);
	};

	/**
	 *
	 * @param event The contact contact changed
	 * @returns
	 */
	const handleContactChanged = (event: { contactId: string }) => {
		const contact = getContactByID(event.contactId);
		const type = contact.getStatus().type;
		switch (type) {
			case connect.ContactStateType.ENDED:
			case connect.ContactStateType.ERROR:
			case connect.ContactStateType.CONNECTED: {
				switchToContact(event.contactId);
				return;
			}
		}
		switchToContact(null);
	};

	/**
	 * The chat has ended, but the contact is not yet destroyed or closed
	 * @param contact
	 */
	const handleContactEnded = async (contact: connect.Contact) => {
		if (contact?.contactId && contact?.contactId === contactIDRef.current) {
			const state = getContactByID(contactIDRef.current)?.getState();
			setContactState(state);
			dispatchEvent(AWSConnectProctorSharedEvents.CONTACT_ENDED, await getLatestContactState());
		}

		setContactActive(false);
	};

	/**
	 * The contact is fully closed and destroyed
	 * @param event
	 */
	const handleContactDestroyed = (contact: connect.Contact) => {
		if (contact?.contactId && contact?.contactId === contactIDRef.current) {
			dispatchEvent(AWSConnectProctorSharedEvents.CONTACT_DESTROYED, {
				contactID: contact.contactId,
				contactState: {
					type: "ended",
					...contact?.getState()
				}
			});
			//give the iframe time to unload/accept the destroyed event
			setTimeout(() => {
				clearContact();
			}, 1000);
		}
	};

	const getCurrentContact = (): connect.Contact | null => {
		return getContactByID(contactIDRef.current);
	};

	const getContactByID = (contactId: string): connect.Contact => {
		if (!agent.current) {
			logger.warn("getContactByID", "NO AGENT");
			return null;
		}

		return agent.current.getContacts().find((contact) => contact.contactId === contactId);
	};

	/**
	 * used by messaging provider
	 * @param content
	 * @returns
	 */
	const sendChatMessage = async (content: string) => {
		const contact = getCurrentContact();

		if (!contact) {
			return;
		}

		const connection = contact.getAgentConnection() as connect.ChatConnection;
		const chatSession = (await connection.getMediaController()) as connect.ChatSession;
		await chatSession.sendMessage({
			contentType: "text/plain",
			message: content
		});
		logger.debug("AWSConnectAgentGlobalProviderComponent sendChatMessage", { content });
	};

	const value: AWSConnectAgentContext = {
		contactAttributes,
		contactID,
		contactState,
		authenticated,
		loadingContact,
		initializeAgent,
		addListener,
		removeListener,
		proctorApplicationRef,
		connectAgentContainerRef,
		contactActive
	};
	return <AWSConnectAgentContext.Provider value={value}>{props.children}</AWSConnectAgentContext.Provider>;
};
